package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import com.example.borshi.web.nutritionist.models.NutritionistBasePlan
import com.example.borshi.web.nutritionist.models.NutritionalTargets
import com.example.borshi.web.nutritionist.models.PlanCategory
import com.example.borshi.web.nutritionist.models.PricingTemplate
import com.example.borshi.web.nutritionist.repository.NutritionistRepository
import com.example.borshi.web.nutritionist.repository.ProductItem
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Enhanced Create Plan Modal with product selection and nutrition calculator
 */
@Composable
fun CreatePlanModal(
    isOpen: Boolean,
    repository: NutritionistRepository,
    nutritionistId: String,
    nutritionistName: String,
    onClose: () -> Unit,
    onPlanCreated: () -> Unit
) {
    if (!isOpen) return

    // Basic Info
    var planName by remember { mutableStateOf("") }
    var category by remember { mutableStateOf("GENERAL_HEALTH") }
    var description by remember { mutableStateOf("") }
    var targetAudience by remember { mutableStateOf("") }
    var goals by remember { mutableStateOf("") }

    // Nutritional Targets
    var proteinPerWeek by remember { mutableStateOf("") }
    var caloriesPerDay by remember { mutableStateOf("") }
    var omega3PerWeek by remember { mutableStateOf("") }

    // Pricing
    var weeklyPrice by remember { mutableStateOf("") }
    var isPublic by remember { mutableStateOf(false) }

    // Product Selection
    var selectedProducts by remember { mutableStateOf<List<Pair<ProductItem, Int>>>(emptyList()) } // Product + Quantity
    var showProductBrowser by remember { mutableStateOf(false) }
    var availableProducts by remember { mutableStateOf<List<ProductItem>>(emptyList()) }
    var isLoadingProducts by remember { mutableStateOf(false) }

    var isSubmitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentStep by remember { mutableStateOf(1) } // 1=Basic Info, 2=Nutrition & Products, 3=Review

    val scope = rememberCoroutineScope()

    // Load products when modal opens
    LaunchedEffect(isOpen) {
        if (isOpen && availableProducts.isEmpty()) {
            scope.launch {
                isLoadingProducts = true
                repository.searchProducts("", emptyMap())
                    .onSuccess {
                        availableProducts = it
                        console.log("✅ Loaded ${it.size} products")
                    }
                    .onFailure { console.error("❌ Error loading products:", it) }
                    .also { isLoadingProducts = false }
            }
        }
    }

    // Calculate cumulative nutrition from selected products
    val cumulativeNutrition = remember(selectedProducts) {
        selectedProducts.fold(Triple(0.0, 0.0, 0.0)) { acc, (product, qty) ->
            Triple(
                acc.first + (product.protein * qty / 100),  // Total protein
                acc.second + (product.calories * qty / 100),  // Total calories
                acc.third + (product.omega3 * qty / 100)     // Total omega-3
            )
        }
    }

    // Modal Overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
            padding(20.px)
        }
        onClick { onClose() }
    }) {
        // Modal Content
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(12.px)
                maxWidth(900.px)
                width(100.percent)
                maxHeight(90.vh)
                overflowY("auto")
                property("box-shadow", "0 4px 20px rgba(0,0,0,0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header with Steps
            Div({
                style {
                    padding(20.px)
                    property("border-bottom", "1px solid #eee")
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(16.px)
                    }
                }) {
                    H2({ style { margin(0.px); fontSize(24.px); color(Color("#333")) } }) {
                        Text("📋 Create Nutrition Plan")
                    }
                    Button({
                        style {
                            backgroundColor(Color("transparent"))
                            border(0.px)
                            fontSize(24.px)
                            cursor("pointer")
                            padding(0.px)
                            width(32.px)
                            height(32.px)
                        }
                        onClick { onClose() }
                    }) {
                        Text("×")
                    }
                }

                // Step Indicator
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(16.px)
                    }
                }) {
                    StepIndicator(1, currentStep, "Basic Info")
                    StepIndicator(2, currentStep, "Products & Nutrition")
                    StepIndicator(3, currentStep, "Review & Create")
                }
            }

            // Form Content - Step Based
            Div({ style { padding(20.px) } }) {
                if (error != null) {
                    ErrorMessage(error!!)
                }

                when (currentStep) {
                    1 -> Step1BasicInfo(
                        planName, { planName = it },
                        category, { category = it },
                        description, { description = it },
                        targetAudience, { targetAudience = it },
                        goals, { goals = it }
                    )
                    2 -> Step2ProductsNutrition(
                        proteinPerWeek, { proteinPerWeek = it },
                        caloriesPerDay, { caloriesPerDay = it },
                        omega3PerWeek, { omega3PerWeek = it },
                        weeklyPrice, { weeklyPrice = it },
                        selectedProducts,
                        { selectedProducts = it },
                        availableProducts,
                        isLoadingProducts,
                        cumulativeNutrition
                    )
                    3 -> Step3Review(
                        planName, category, description, targetAudience, goals,
                        proteinPerWeek, caloriesPerDay, omega3PerWeek,
                        weeklyPrice, selectedProducts, cumulativeNutrition
                    )
                }
            }

            // Footer with Navigation
            Div({
                style {
                    padding(20.px)
                    property("border-top", "1px solid #eee")
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    gap(12.px)
                }
            }) {
                // Back/Cancel Button
                Button({
                    style {
                        padding(10.px, 20.px)
                        backgroundColor(Color("transparent"))
                        border(1.px, LineStyle.Solid, Color("#ddd"))
                        borderRadius(6.px)
                        cursor("pointer")
                        fontSize(14.px)
                    }
                    onClick {
                        if (currentStep > 1) {
                            currentStep--
                        } else {
                            onClose()
                        }
                    }
                }) {
                    Text(if (currentStep > 1) "← Back" else "Cancel")
                }

                // Next/Create Button
                Button({
                    style {
                        padding(10.px, 20.px)
                        backgroundColor(if (isSubmitting) Color("#ccc") else Color("#2196F3"))
                        color(Color.white)
                        border(0.px)
                        borderRadius(6.px)
                        cursor(if (isSubmitting) "not-allowed" else "pointer")
                        fontSize(14.px)
                        fontWeight("600")
                    }
                    if (isSubmitting) {
                        attr("disabled", "disabled")
                    }
                    onClick {
                        if (currentStep < 3) {
                            // Validate current step
                            when (currentStep) {
                                1 -> {
                                    if (planName.isBlank() || description.isBlank()) {
                                        error = "Plan name and description are required"
                                        return@onClick
                                    }
                                    error = null
                                    currentStep++
                                }
                                2 -> {
                                    if (proteinPerWeek.isBlank() || caloriesPerDay.isBlank()) {
                                        error = "Please set nutritional targets"
                                        return@onClick
                                    }
                                    error = null
                                    currentStep++
                                }
                            }
                        } else {
                            // Step 3: Create plan
                            scope.launch {
                                isSubmitting = true
                                error = null

                                try {
                                    val plan = NutritionistBasePlan(
                                        basePlanId = "",  // Auto-generated by Firebase
                                        nutritionistId = nutritionistId,
                                        nutritionistName = nutritionistName,
                                        planName = planName,
                                        category = PlanCategory.valueOf(category),
                                        description = description,
                                        targetAudience = targetAudience,
                                        goals = goals.split(",").map { it.trim() }.filter { it.isNotEmpty() },
                                        nutritionalTargets = NutritionalTargets(
                                            proteinPerWeek = proteinPerWeek.toIntOrNull() ?: 0,
                                            caloriesPerDay = caloriesPerDay.toIntOrNull() ?: 0,
                                            omega3PerWeek = omega3PerWeek.toIntOrNull() ?: 0
                                        ),
                                        pricingTemplate = PricingTemplate(
                                            weeklyBase = weeklyPrice.toDoubleOrNull() ?: 0.0
                                        ),
                                        imageUrl = "",  // Optional
                                        badgeText = "",  // Optional
                                        badgeColor = "",  // Optional
                                        isPublic = isPublic,
                                        isActive = true,
                                        createdAt = kotlin.js.Date.now().toLong(),
                                        updatedAt = kotlin.js.Date.now().toLong()
                                    )

                                    repository.createBasePlan(plan)
                                        .onSuccess { planId ->
                                            console.log("✅ Plan created with ID: $planId")
                                            onPlanCreated()
                                            onClose()
                                        }
                                        .onFailure {
                                            error = "Failed to create plan: ${it.message}"
                                            isSubmitting = false
                                        }
                                } catch (e: Exception) {
                                    error = "Error: ${e.message}"
                                    isSubmitting = false
                                }
                            }
                        }
                    }
                }) {
                    Text(when {
                        isSubmitting -> "Creating..."
                        currentStep < 3 -> "Next →"
                        else -> "Create Plan"
                    })
                }
            }
        }
    }
}

// Step Indicator Component
@Composable
private fun StepIndicator(step: Int, currentStep: Int, label: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            opacity(if (step <= currentStep) 1.0 else 0.4)
        }
    }) {
        Div({
            style {
                width(32.px)
                height(32.px)
                borderRadius(50.percent)
                backgroundColor(if (step <= currentStep) Color("#2196F3") else Color("#ddd"))
                color(Color.white)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontWeight("600")
            }
        }) {
            Text(step.toString())
        }
        Span({ style { fontSize(14.px); fontWeight(if (step == currentStep) "600" else "normal") } }) {
            Text(label)
        }
    }
}

// Step 1: Basic Information
@Composable
private fun Step1BasicInfo(
    planName: String, onPlanNameChange: (String) -> Unit,
    category: String, onCategoryChange: (String) -> Unit,
    description: String, onDescriptionChange: (String) -> Unit,
    targetAudience: String, onTargetAudienceChange: (String) -> Unit,
    goals: String, onGoalsChange: (String) -> Unit
) {
    FormSection("Plan Details") {
        FormField("Plan Name *", "planName") {
            Input(InputType.Text) {
                value(planName)
                onInput { onPlanNameChange(it.value) }
                attr("placeholder", "e.g., High Protein Muscle Building Plan")
            }
        }

        FormField("Category *", "category") {
            Select({
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Color("#ddd"))
                    borderRadius(6.px)
                    fontSize(14.px)
                }
                onChange { onCategoryChange(it.value ?: "GENERAL_HEALTH") }
            }) {
                Option("GENERAL_HEALTH", {
                    if (category == "GENERAL_HEALTH") attr("selected", "selected")
                }) { Text("General Health & Wellness") }
                Option("MUSCLE_BUILDING", {
                    if (category == "MUSCLE_BUILDING") attr("selected", "selected")
                }) { Text("Muscle Building") }
                Option("WEIGHT_LOSS", {
                    if (category == "WEIGHT_LOSS") attr("selected", "selected")
                }) { Text("Weight Loss") }
                Option("DIABETES", {
                    if (category == "DIABETES") attr("selected", "selected")
                }) { Text("Diabetes Management") }
                Option("HEART_HEALTH", {
                    if (category == "HEART_HEALTH") attr("selected", "selected")
                }) { Text("Heart Health") }
                Option("PCOS", {
                    if (category == "PCOS") attr("selected", "selected")
                }) { Text("PCOS Management") }
            }
        }

        FormField("Description *", "description") {
            TextArea(attrs = {
                value(description)
                onInput { onDescriptionChange(it.value) }
                attr("placeholder", "Detailed description of the plan and its benefits...")
                attr("rows", "4")
                style {
                    width(100.percent)
                    padding(10.px)
                    border(1.px, LineStyle.Solid, Color("#ddd"))
                    borderRadius(6.px)
                    fontSize(14.px)
                    fontFamily("inherit")
                }
            })
        }

        FormField("Target Audience", "audience") {
            Input(InputType.Text) {
                value(targetAudience)
                onInput { onTargetAudienceChange(it.value) }
                attr("placeholder", "e.g., Athletes, Weight loss seekers, PCOS patients")
            }
        }

        FormField("Health Goals", "goals") {
            Input(InputType.Text) {
                value(goals)
                onInput { onGoalsChange(it.value) }
                attr("placeholder", "e.g., Build muscle, Improve energy, Control blood sugar")
            }
            Span({ style { fontSize(12.px); color(Color("#999")) } }) {
                Text("Comma-separated goals")
            }
        }
    }
}

// Step 2: Products & Nutrition
@Composable
private fun Step2ProductsNutrition(
    proteinPerWeek: String, onProteinChange: (String) -> Unit,
    caloriesPerDay: String, onCaloriesChange: (String) -> Unit,
    omega3PerWeek: String, onOmega3Change: (String) -> Unit,
    weeklyPrice: String, onPriceChange: (String) -> Unit,
    selectedProducts: List<Pair<ProductItem, Int>>,
    onSelectedProductsChange: (List<Pair<ProductItem, Int>>) -> Unit,
    availableProducts: List<ProductItem>,
    isLoadingProducts: Boolean,
    cumulativeNutrition: Triple<Double, Double, Double>
) {
    // Nutritional Targets
    FormSection("Nutritional Targets") {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(3, 1fr)")
                gap(12.px)
            }
        }) {
            FormField("Protein/Week (g) *", "protein") {
                Input(InputType.Number) {
                    value(proteinPerWeek)
                    onInput { onProteinChange(it.value?.toString() ?: "") }
                    attr("placeholder", "1200")
                    attr("min", "0")
                }
            }
            FormField("Calories/Day (kcal) *", "calories") {
                Input(InputType.Number) {
                    value(caloriesPerDay)
                    onInput { onCaloriesChange(it.value?.toString() ?: "") }
                    attr("placeholder", "1800")
                    attr("min", "0")
                }
            }
            FormField("Omega-3/Week (mg)", "omega3") {
                Input(InputType.Number) {
                    value(omega3PerWeek)
                    onInput { onOmega3Change(it.value?.toString() ?: "") }
                    attr("placeholder", "2500")
                    attr("min", "0")
                }
            }
        }
    }

    // Weekly Price
    FormSection("Pricing") {
        FormField("Weekly Price (৳) *", "price") {
            Input(InputType.Number) {
                value(weeklyPrice)
                onInput { onPriceChange(it.value?.toString() ?: "") }
                attr("placeholder", "2500")
                attr("min", "0")
                attr("step", "100")
            }
        }
    }

    // Product Selection (Simplified)
    FormSection("Recommended Products (Optional)") {
        Div({
            style {
                backgroundColor(Color("#f8f9fa"))
                padding(16.px)
                borderRadius(8.px)
                marginBottom(12.px)
            }
        }) {
            Div({ style { fontSize(14.px); color(Color("#666")); marginBottom(8.px) } }) {
                Text("💡 Product selection will be available in the next update")
            }
            Div({ style { fontSize(12.px); color(Color("#999")) } }) {
                Text("You'll be able to add specific seafood products to this plan and calculate cumulative nutrition")
            }
        }
    }

    // Show cumulative if products selected
    if (selectedProducts.isNotEmpty()) {
        FormSection("Cumulative Nutrition from Selected Products") {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(3, 1fr)")
                    gap(12.px)
                }
            }) {
                NutritionCard("Protein", "${cumulativeNutrition.first.toInt()}g", "#4CAF50")
                NutritionCard("Calories", "${cumulativeNutrition.second.toInt()} kcal", "#FF9800")
                NutritionCard("Omega-3", "${cumulativeNutrition.third.toInt()}mg", "#2196F3")
            }
        }
    }
}

// Step 3: Review & Confirm
@Composable
private fun Step3Review(
    planName: String, category: String, description: String,
    targetAudience: String, goals: String,
    proteinPerWeek: String, caloriesPerDay: String, omega3PerWeek: String,
    weeklyPrice: String,
    selectedProducts: List<Pair<ProductItem, Int>>,
    cumulativeNutrition: Triple<Double, Double, Double>
) {
    FormSection("Review Your Plan") {
        // Basic Info
        ReviewItem("Plan Name", planName)
        ReviewItem("Category", getCategoryDisplay(category))
        ReviewItem("Description", description)
        if (targetAudience.isNotBlank()) ReviewItem("Target Audience", targetAudience)
        if (goals.isNotBlank()) ReviewItem("Goals", goals)

        Div({ style { height(1.px); backgroundColor(Color("#eee")); margin(16.px, 0.px) } })

        // Nutrition
        ReviewItem("Protein Target", "$proteinPerWeek g/week")
        ReviewItem("Calories Target", "$caloriesPerDay kcal/day")
        if (omega3PerWeek.isNotBlank()) ReviewItem("Omega-3 Target", "$omega3PerWeek mg/week")

        Div({ style { height(1.px); backgroundColor(Color("#eee")); margin(16.px, 0.px) } })

        // Pricing
        ReviewItem("Weekly Price", "৳$weeklyPrice")

        if (selectedProducts.isNotEmpty()) {
            Div({ style { height(1.px); backgroundColor(Color("#eee")); margin(16.px, 0.px) } })
            ReviewItem("Selected Products", "${selectedProducts.size} products")
        }

        // Success Message
        Div({
            style {
                backgroundColor(Color("#E8F5E9"))
                padding(16.px)
                borderRadius(8.px)
                marginTop(20.px)
                property("border-left", "4px solid #4CAF50")
            }
        }) {
            P({ style { margin(0.px); fontSize(14.px); color(Color("#2E7D32")); fontWeight("600") } }) {
                Text("✓ Plan is ready to be created!")
            }
            P({ style { margin(8.px, 0.px, 0.px); fontSize(13.px); color(Color("#666")) } }) {
                Text("Click 'Create Plan' to save this nutrition plan to your library.")
            }
        }
    }
}

// Helper Components
@Composable
private fun NutritionCard(label: String, value: String, color: String) {
    Div({
        style {
            backgroundColor(Color.white)
            padding(12.px)
            borderRadius(8.px)
            border(1.px, LineStyle.Solid, Color("#eee"))
            textAlign("center")
        }
    }) {
        Div({ style { fontSize(18.px); fontWeight("bold"); color(Color(color)); marginBottom(4.px) } }) {
            Text(value)
        }
        Div({ style { fontSize(12.px); color(Color("#999")) } }) {
            Text(label)
        }
    }
}

@Composable
private fun ReviewItem(label: String, value: String) {
    Div({
        style {
            marginBottom(12.px)
        }
    }) {
        Div({ style { fontSize(12.px); color(Color("#999")); marginBottom(4.px) } }) {
            Text(label)
        }
        Div({ style { fontSize(14.px); color(Color("#333")) } }) {
            Text(value)
        }
    }
}

private fun getCategoryDisplay(category: String): String {
    return when (category) {
        "GENERAL_HEALTH" -> "General Health & Wellness"
        "MUSCLE_BUILDING" -> "Muscle Building"
        "WEIGHT_LOSS" -> "Weight Loss"
        "DIABETES" -> "Diabetes Management"
        "HEART_HEALTH" -> "Heart Health"
        "PCOS" -> "PCOS Management"
        else -> category
    }
}
